// src/components/task/MemberAssignmentItem.tsx
import React, { useCallback, useEffect, useMemo } from "react";
import { UserOrganisationType } from "@/api/query/useOrganisationDetailsQuery";
import {
  useEffortCalculation,
  calculateTaskEffort,
  calculateTotalHours,
} from "@/hooks/useEffortCalculation";
import InputNumber from "@/components/common/InputNumber";
import UserAvatar from "../ui/userAvatar";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";
import { Task } from "@/api/mutation/useTaskCreateMutation";
import crossNew from "@/assets/svg/crossNew.svg";
import { countLeaveDaysInRange } from "@/helperFuntions/checkUserLeave";

type AssignedUser = {
  taskAssignUsersId: string;
  assignedByUserId: string;
  assignedToUserId: string;
  workingHours: number;
  taskEffort: number;
};

type Props = {
  task: Task;
  member: UserOrganisationType;
  assignedInfo: AssignedUser | undefined;
  taskDuration: number;
  isAssigned: boolean;
  onToggleAssign: (member: UserOrganisationType, workingHours: number) => void;
  onUpdateAssign: (
    member: UserOrganisationType,
    workingHours: number,
    taskEffort: number
  ) => void;
};

const workingHoursPerDay = 8;

const MemberAssignmentItem = ({
  task,
  member,
  assignedInfo,
  taskDuration,
  isAssigned,
  onToggleAssign,
  onUpdateAssign,
}: Props) => {
  const { t } = useTranslation();

  // Task start and end
  const taskStart = new Date(task?.startDate);
  const taskEnd = new Date(task?.endDate);

  // Count leave days overlapping with task
  const leaveDays = useMemo(() => {
    return countLeaveDaysInRange(
      member.user.userAvailability ?? [],
      taskStart,
      taskEnd
    );
  }, [task]);

  // Effective duration after removing leave days

  const adjustedDuration = Math.max(0, taskDuration - leaveDays);

  // Check if user is fully unavailable (all days are leave days)
  const isFullyUnavailable = adjustedDuration <= 0;

  // Initial effort
  const initialEffort = useMemo(() => {
    if (assignedInfo) {
      return calculateTaskEffort(
        assignedInfo.workingHours,
        adjustedDuration,
        workingHoursPerDay
      );
    }
    return 100;
  }, [assignedInfo, adjustedDuration]);

  // Hook to calculate effort/hours
  const { taskEffort, totalHours, duration, setTaskEffort, setTotalHours } =
    useEffortCalculation(
      initialEffort ?? 100,
      adjustedDuration,
      assignedInfo?.workingHours ?? 8
    );
    console.log(duration);
    
  // Sync hours when assignment changes
  useEffect(() => {
    const newEffort = taskEffort ?? 100;
    const newHours = calculateTotalHours(
      newEffort,
      adjustedDuration,
      workingHoursPerDay
    );
    setTotalHours(assignedInfo?.workingHours ?? newHours);
  }, [assignedInfo?.workingHours, adjustedDuration]);

  // Debounced update callback
  const debouncedUpdate = useCallback(
    debounce((newHours: number, newEffort: number) => {
      onUpdateAssign(member, newHours, newEffort);
    }, 800),
    [member, onUpdateAssign]
  );

  // Handlers
  const handleEffortChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEffort = Math.min(Number(e.target.value), 100);
    const newHours = calculateTotalHours(
      newEffort,
      adjustedDuration,
      workingHoursPerDay
    );
    setTaskEffort(newEffort);
    setTotalHours(newHours);
    debouncedUpdate(newHours, newEffort);
  };

  const handleHoursChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputHours = Number(e.target.value);
    const calculatedEffort = calculateTaskEffort(
      inputHours,
      adjustedDuration,
      workingHoursPerDay
    );

    if (calculatedEffort > 100) {
      const clampedHours = calculateTotalHours(
        100,
        adjustedDuration,
        workingHoursPerDay
      );
      setTotalHours(clampedHours);
      setTaskEffort(100);
      debouncedUpdate(clampedHours, 100);
    } else {
      setTotalHours(inputHours);
      setTaskEffort(calculatedEffort);
      debouncedUpdate(inputHours, calculatedEffort);
    }
  };

  return (
    <div
      className={`flex flex-col p-1 my-1 gap-2 hover:bg-slate-100 rounded-md select-none ${
        isAssigned && member.user.isActive ? "bg-slate-100/80" : ""
      } ${
        member.user.isActive
          ? ""
          : "bg-gray-400/30 opacity-70 cursor-not-allowed"
      }`}
    >
      {/* Main Row */}
      <div className="flex items-center gap-4">
        <div
          className={`flex gap-2 items-center ${
            member.user.isActive ? "cursor-pointer" : "cursor-not-allowed"
          }`}
          onClick={() => {
            if (!member.user.isActive) return;
            onToggleAssign(member, totalHours);
          }}
        >
          <UserAvatar user={member.user} className="rounded-full" />
          <div>
            <div>
              {member.user.firstName} {member.user.lastName}
            </div>
            <div className="text-sm text-gray-400">{member.user.email}</div>
          </div>
        </div>

        <div className="flex justify-end items-center w-full">
          {isAssigned && !task.subtasks?.length && !task.milestoneIndicator && (
            <div className="grid grid-cols-3 gap-2">
              {/* Effort (%) */}
              <div className="flex flex-col items-center justify-center">
                <label className="text-xs">{t("Task Effort (%)")}</label>
                <InputNumber
                  min={0}
                  max={100}
                  value={taskEffort <= 0 ? 0 : taskEffort}
                  disabled={
                    isFullyUnavailable || !member.user.isActive
                  }
                  className="w-16 px-2 !text-sm !py-1 h-[30px]"
                  onChange={handleEffortChange}
                />
              </div>

              {/* Duration */}
              <div className="flex flex-col items-center justify-center">
                <label className="text-xs">{t("Duration (days)")}</label>
                <div className="flex flex-col items-center">
                  <InputNumber
                    min={1}
                    value={adjustedDuration <= 0 ? 0 : adjustedDuration}
                    disabled
                    className="w-16 px-2 !text-sm !py-1 h-[30px]"
                  />
                </div>
              </div>

              {/* Hours */}
              <div className="flex flex-col items-center justify-center">
                <label className="text-xs">{t("Total Hours")}</label>
                <InputNumber
                  min={0}
                  value={totalHours <= 0 ? 0 : totalHours}
                  disabled={
                    isFullyUnavailable || !member.user.isActive
                  }
                  className="w-16 px-2 !text-sm !py-1 h-[30px]"
                  onChange={handleHoursChange}
                />
              </div>
            </div>
          )}
          {!isAssigned ? (
            <input
              type="checkbox"
              className={`accent-black ${
                isAssigned && member.user.isActive ? "invert" : ""
              } ${
                member.user.isActive ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onChange={() => {
                onToggleAssign(member, totalHours);
              }}
              checked={isAssigned}
              disabled={!member.user.isActive}
            />
          ) : (
            <img
              src={crossNew}
              className={`w-4 min-w-4 ${
                member.user.isActive ? "cursor-pointer" : "cursor-not-allowed"
              }`}
              onClick={() => {
                if (!member.user.isActive) return;
                onToggleAssign(member, totalHours);
              }}
              style={{
                filter:
                  "invert(0%) sepia(100%) saturate(10000%) hue-rotate(287deg)",
              }}
            />
          )}
        </div>
      </div>

      {/* Error/Info message if user has leave */}
      {leaveDays > 0 && (
        <div className="ml-10 text-xs text-red-500">
        ⚠️{t("availabilityMessage", { leaveDays })}
        </div>
      )}
    </div>
  );
};

export default MemberAssignmentItem;
